import type { LucideIcon } from 'lucide-react';
import {
  BellIcon,
  CoinsIcon,
  CpuIcon,
  FolderIcon,
  GaugeIcon,
  KeyRoundIcon,
  LayersIcon,
  LibraryIcon,
  PlugZapIcon,
  ScrollTextIcon,
  Settings2Icon,
  HistoryIcon,
  ContainerIcon,
  TagIcon,
  UsersIcon,
  SparklesIcon,
  ShieldIcon,
  SlidersHorizontalIcon,
  ListChecksIcon,
  WorkflowIcon,
  ZapIcon,
} from 'lucide-react';

/**
 * Every address here is workspace-scoped and takes a single `workspaceId`
 * parameter, which is what lets the sidebar render them uniformly.
 */
export type WorkspaceNavPath =
  | '/workspaces/$workspaceId'
  | '/workspaces/$workspaceId/files'
  | '/workspaces/$workspaceId/files/agents-skills'
  | '/workspaces/$workspaceId/workflows'
  | '/workspaces/$workspaceId/boards'
  | '/workspaces/$workspaceId/tasks'
  | '/workspaces/$workspaceId/library'
  | '/workspaces/$workspaceId/integrations'
  | '/workspaces/$workspaceId/integrations/connections'
  | '/workspaces/$workspaceId/integrations/triggers'
  | '/workspaces/$workspaceId/integrations/secrets'
  | '/workspaces/$workspaceId/ai-usage'
  | '/workspaces/$workspaceId/ai-usage/models'
  | '/workspaces/$workspaceId/ai-usage/spend'
  | '/workspaces/$workspaceId/audit'
  | '/workspaces/$workspaceId/settings'
  | '/workspaces/$workspaceId/settings/general'
  | '/workspaces/$workspaceId/settings/people'
  | '/workspaces/$workspaceId/settings/activity'
  | '/workspaces/$workspaceId/settings/labels'
  | '/workspaces/$workspaceId/settings/images';

/**
 * The console's addresses, which take no parameters at all.
 *
 * A separate union rather than more members of `WorkspaceNavPath`: these sit above the
 * workspace boundary, and one type spanning both would let a console entry be rendered
 * by the workspace sidebar, which interpolates a `workspaceId` into every `to` it is
 * given.
 */
export type AdminNavPath =
  | '/admin'
  | '/admin/workspaces'
  | '/admin/ai-providers'
  | '/admin/limits'
  | '/admin/linked-accounts'
  | '/admin/notifications'
  | '/admin/general';

export interface AdminNavItem {
  label: string;
  to: AdminNavPath;
  icon: LucideIcon;
  /** Current only on an exact match. Used by the console root. */
  exact?: boolean;
  visibleTo?: NavGate;
}

/** Which count from the workspace aggregate an entry displays, if any. */
export type NavBadge = 'workflows' | 'boards' | 'tasks';

/**
 * What a tab of a grouped entry says beside its name (spec 227 FR-010): how many
 * are in it, then who waits for an answer, which is the one that asks for
 * something. Figures, not markup, so a `NavItem` stays a plain record the palette
 * and the workspace switch can walk; the strip draws them.
 */
export interface TabBadge {
  count?: number;
  attention?: {
    count: number;
    /** Read after the number: "waiting to join". */
    label: string;
  };
}

export interface NavItem {
  label: string;
  to: WorkspaceNavPath;
  icon: LucideIcon;
  badge?: NavBadge;
  /** Set by the section that knows the figures, never in the lists below. */
  tabBadge?: TabBadge;
  /** Current only on an exact match. Used by the workspace root. */
  exact?: boolean;
  /**
   * Who may reach this destination (spec 072 FR-003, 073 FR-003).
   *
   * Absent means everyone. It is declared *here* rather than filtered at render for a
   * specific reason: `ALL_NAV_ITEMS` below is walked by tests as the list of places a
   * user can go, and a filter applied in the component would leave that constant
   * asserting something no longer true.
   *
   * This is a statement about what the navigation *draws*, never about what is
   * allowed. The service refuses regardless; hiding a door nobody may open is
   * courtesy, not security.
   */
  visibleTo?: NavGate;
}

/**
 * Every condition a destination may be gated on.
 *
 * A closed set rather than a predicate, so the two conditional surfaces the product
 * has — the workspace Usage page and the organization console — cannot arrive with two
 * different mechanisms, which is how they drift (073 FR-003). Adding a third gate is a
 * deliberate edit here and a matching field on `NavViewer` below.
 */
export type NavGate =
  | 'workspace-owner'
  | 'org-admin'
  | 'workspace-owner-or-org-admin'
  /** Its people and org admins: not a non-member reading an Organization workspace (spec 188). */
  | 'workspace-member'
  /** A section that is still a placeholder; offered only to a build that asks (spec 099). */
  | 'unbuilt';

/** What is known about the person the navigation is being drawn for. */
export interface NavViewer {
  isWorkspaceOwner: boolean;
  isOrgAdmin: boolean;
  /**
   * May work in the workspace (spec 188): false only for someone reading an
   * Organization workspace they are not in. Absent outside a workspace.
   */
  isWorkspaceMember?: boolean;
  /** Whether placeholder sections are offered; off unless the build says so. */
  showUnbuilt?: boolean;
}

/**
 * The main areas, in the prototype's order.
 *
 * Documents and Environments are deliberately absent. The prototype predates
 * the current product requirements, which fold the Documents Repository into
 * Workspace Files and replace named execution environments with one generic
 * sandbox image.
 *
 * The prototype labelled Workspace Files "Explorer". One name is used
 * everywhere instead — sidebar, breadcrumb and page heading all say "Files",
 * which is what the route already is; the types and code behind it keep the
 * canonical name.
 */
export const PRIMARY_NAV: NavItem[] = [
  {
    label: 'Overview',
    to: '/workspaces/$workspaceId',
    icon: GaugeIcon,
    exact: true,
  },
  { label: 'Files', to: '/workspaces/$workspaceId/files', icon: FolderIcon },
  {
    // One entry, not two (spec 094 FR-001, FR-006).
    //
    // `Boards` used to sit directly beneath this row, and behind the two of them
    // was one thing: a board is what a published workflow looks like once work is
    // moving through it. Users could not tell them apart because there was
    // nothing to tell apart — and the split also meant neither badge described
    // the workspace and the palette answered every search twice.
    //
    // The badge is carried on the row itself because the row folds: with the list
    // away, this figure is the only thing saying how much was put away. It counts
    // every workflow, drafts included, so it agrees with both the list it folds
    // and the page it links to (FR-004).
    label: 'Workflows',
    to: '/workspaces/$workspaceId/workflows',
    icon: WorkflowIcon,
    badge: 'workflows',
  },
  {
    label: 'Tasks',
    to: '/workspaces/$workspaceId/tasks',
    icon: ListChecksIcon,
    // After Boards, because it answers the question a board cannot: a board is
    // one workflow, and this spans them. The count is not decoration — every
    // other entry hiding a collection carries one, and a row without it would be
    // the only navigational dead reckoning in the sidebar.
    badge: 'tasks',
  },
  {
    label: 'Library',
    to: '/workspaces/$workspaceId/library',
    icon: LibraryIcon,
    // Built by spec 104: no longer gated behind the unbuilt-sections flag.
  },
];

/**
 * Workspace configuration and history, under their own section label.
 *
 * Three entries where there were seven (spec 224): pages that belong together are
 * tabs of one entry. Integrations holds what connects the workspace to the outside
 * — its connections, the triggers that start work from them, and the secrets both
 * use; AI usage holds the models (everyone) and their spend (owners and admins);
 * Execution images became a Settings tab. Each tab keeps the gate its page had,
 * declared in the section's own list below.
 */
export const SECONDARY_NAV: NavItem[] = [
  {
    label: 'Agents & skills',
    to: '/workspaces/$workspaceId/files/agents-skills',
    icon: SparklesIcon,
    visibleTo: 'unbuilt',
  },
  {
    // No gate: Triggers is everyone's, so a reader still has a tab to open.
    label: 'Integrations',
    to: '/workspaces/$workspaceId/integrations',
    icon: PlugZapIcon,
  },
  {
    // No gate: Models is everyone's (spec 215); Spend is gated as its own tab.
    label: 'AI usage',
    to: '/workspaces/$workspaceId/ai-usage',
    icon: CpuIcon,
  },
  {
    label: 'Audit trail',
    to: '/workspaces/$workspaceId/audit',
    icon: ScrollTextIcon,
    visibleTo: 'unbuilt',
  },
  {
    label: 'Settings',
    to: '/workspaces/$workspaceId/settings',
    icon: Settings2Icon,
    // Its people and its labels are the members' business (spec 188).
    visibleTo: 'workspace-member',
  },
];

/**
 * The tabs of a sidebar entry that groups several pages (spec 224), in strip order.
 *
 * Each tab is a page with its own address, so links, Back and a shared URL keep
 * working, and each carries the gate its page had as a sidebar entry. They are
 * `NavItem`s for that reason: `visibleNavItems` filters them like any destination,
 * and the command palette and the workspace switch walk them with the rest.
 */
export const INTEGRATIONS_TABS: NavItem[] = [
  {
    label: 'Connections',
    to: '/workspaces/$workspaceId/integrations/connections',
    icon: PlugZapIcon,
    visibleTo: 'workspace-member',
  },
  {
    label: 'Triggers',
    to: '/workspaces/$workspaceId/integrations/triggers',
    icon: ZapIcon,
  },
  {
    label: 'Secrets',
    to: '/workspaces/$workspaceId/integrations/secrets',
    icon: KeyRoundIcon,
    // Members read them, owners change them; a non-member never sees them, even in
    // an Organization workspace (decision Q10, spec 151 FR-005).
    visibleTo: 'workspace-member',
  },
];

export const AI_USAGE_TABS: NavItem[] = [
  {
    label: 'Models',
    to: '/workspaces/$workspaceId/ai-usage/models',
    icon: CpuIcon,
    // Everyone who can open the workspace, readers included (spec 215): the members'
    // view of what Spend shows owners, without the money.
  },
  {
    label: 'Spend',
    to: '/workspaces/$workspaceId/ai-usage/spend',
    icon: CoinsIcon,
    // The owner, or an organization administrator (spec 075). Spend figures are still
    // not general workspace information — but an administrator already sees every
    // workspace's spend on the console and owns the limits constraining it.
    visibleTo: 'workspace-owner-or-org-admin',
  },
];

/**
 * Settings' tabs (spec 227): each its own address, as the other groups' are, so
 * Back, the breadcrumb and the palette treat them alike. General, Labels and
 * Execution images are everyone's who may open Settings at all; People is the
 * members' (today's `can_work`), Activity its owners' and the org admins' (spec
 * 191, today's `can_manage`).
 */
export const SETTINGS_TABS: NavItem[] = [
  {
    label: 'General',
    to: '/workspaces/$workspaceId/settings/general',
    icon: Settings2Icon,
  },
  {
    label: 'People',
    to: '/workspaces/$workspaceId/settings/people',
    icon: UsersIcon,
    visibleTo: 'workspace-member',
  },
  {
    label: 'Activity',
    to: '/workspaces/$workspaceId/settings/activity',
    icon: HistoryIcon,
    visibleTo: 'workspace-owner-or-org-admin',
  },
  {
    label: 'Labels',
    to: '/workspaces/$workspaceId/settings/labels',
    icon: TagIcon,
  },
  {
    label: 'Execution images',
    to: '/workspaces/$workspaceId/settings/images',
    icon: ContainerIcon,
  },
];

export const SECONDARY_NAV_LABEL = 'Workspace';

/** Flattened, for tests and anything that walks every destination. */
export const ALL_NAV_ITEMS: NavItem[] = [...PRIMARY_NAV, ...SECONDARY_NAV];

/**
 * Every place in a workspace a reader can be sent: the sidebar's entries and the
 * tabs of the grouped ones (spec 224). The command palette offers each, and
 * switching workspace keeps the one open.
 */
export const ALL_DESTINATIONS: NavItem[] = [
  ...ALL_NAV_ITEMS,
  ...INTEGRATIONS_TABS,
  ...AI_USAGE_TABS,
  ...SETTINGS_TABS,
];

/**
 * The console's own sections (spec 073 FR-001, FR-011).
 *
 * A separate list because the console is a separate shell: it sits above the workspace
 * boundary, so none of these paths takes a `workspaceId` and none of them may appear in
 * a workspace's sidebar. Every entry carries the same gate, which is what makes the
 * console reachable as one thing rather than three.
 *
 * **The gate is declared, not positional** (spec 077). It used to be neither: these
 * entries carried no `visibleTo` at all, and were protected only by the fact that
 * `AdminShell` refuses a non-administrator before rendering them. That held for exactly
 * as long as this list had one reader. The command palette is a second, outside that
 * shell, and it found every console section offered to a viewer with no role — so
 * the comment above was describing an intention rather than a fact.
 *
 * A no-op for the console itself, which passes `isOrgAdmin: true` explicitly.
 */
export const ADMIN_NAV: AdminNavItem[] = [
  {
    label: 'Overview',
    to: '/admin',
    icon: GaugeIcon,
    exact: true,
    visibleTo: 'org-admin',
  },
  {
    // Every workspace, its owners and its access log (spec 191).
    label: 'Workspaces',
    to: '/admin/workspaces',
    icon: LayersIcon,
    visibleTo: 'org-admin',
  },
  {
    label: 'AI providers & models',
    to: '/admin/ai-providers',
    icon: SlidersHorizontalIcon,
    visibleTo: 'org-admin',
  },
  {
    label: 'Usage limits',
    to: '/admin/limits',
    icon: CoinsIcon,
    visibleTo: 'org-admin',
  },
  {
    label: 'Commit identity',
    to: '/admin/linked-accounts',
    icon: KeyRoundIcon,
    visibleTo: 'org-admin',
  },
  {
    label: 'Notifications',
    to: '/admin/notifications',
    icon: BellIcon,
    visibleTo: 'org-admin',
  },
  {
    label: 'General',
    to: '/admin/general',
    icon: Settings2Icon,
    visibleTo: 'org-admin',
  },
];

/** The console's entry point, wherever the application offers one. */
export const ADMIN_ENTRY: AdminNavItem = {
  label: 'Organization admin',
  to: '/admin',
  icon: ShieldIcon,
  exact: true,
  visibleTo: 'org-admin',
};

/**
 * What this viewer may see, given who they are.
 *
 * One function for both shells, so a second conditional entry cannot arrive with a
 * second rule. Passing the viewer explicitly — rather than reading identity in here —
 * keeps this module free of data fetching and keeps it testable as the pure
 * list-filtering it is.
 */
export function visibleNavItems<T extends { visibleTo?: NavGate }>(
  items: T[],
  viewer: NavViewer,
): T[] {
  return items.filter((item) => {
    if (item.visibleTo === undefined) return true;
    if (item.visibleTo === 'unbuilt') return viewer.showUnbuilt === true;
    if (item.visibleTo === 'org-admin') return viewer.isOrgAdmin;
    if (item.visibleTo === 'workspace-member') {
      return viewer.isWorkspaceMember ?? true;
    }
    if (item.visibleTo === 'workspace-owner-or-org-admin') {
      return viewer.isWorkspaceOwner || viewer.isOrgAdmin;
    }
    return viewer.isWorkspaceOwner;
  });
}
